import { TrashSimpleIcon, WarningCircleIcon, WarningIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import classNames from 'classnames'
import { useRef, useState } from 'react'
import { match, P } from 'ts-pattern'
import { forbiddenOverride, useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { usePatchCodingGuideline } from '../../utils/api-client/api-hooks.ts'
import { type CodingGuidelinesResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import * as footerStyles from '../drawers/floating-footer.css.ts'
import { FloatingFooter } from '../drawers/floating-footer.tsx'
import { InputContainer } from '../input/input-container.tsx'
import { TextInput } from '../input/text-input.tsx'
import * as styles from './coding-guidelines-drawer.css.ts'
import { formatFileSize, getDocType, getDocTypeBadgeClass } from './coding-guidelines-types.ts'
import { DocTypeIcon } from './doc-type-icon.tsx'
import { CODING_GUIDELINES_DOCUMENT_FAILED_PHASES } from './use-coding-guidelines-document-events.ts'

const RENAME_ERROR_OVERRIDES = { 403: forbiddenOverride(<>You do not have permission to rename documents.</>) }

// #region Types

export type EditFormValues = {
  displayName: string
}

export type EditFormProps = {
  organizationId?: string
  document: CodingGuidelinesResponse
  onSuccess: () => unknown
  onCancel: () => unknown
  onDelete: () => unknown
}

// #endregion

// #region Form Hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: EditFormValues = {
  displayName: '',
}

export const useEditForm = (defaultValueOverrides: Partial<EditFormValues> = {}) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

export const EditForm: React.FC<EditFormProps> = ({ organizationId, document, onSuccess, onCancel, onDelete }) => {
  const form = useFormContext()
  const displayName = useSelector(form.store, state => state.values.displayName)
  const { addToast } = useToastService()
  const drawerContentRef = useRef<HTMLDivElement | null>(null)

  // The backend's coarse DocumentStatus is processing | ready | failed_*; unknown or
  // missing statuses fall back to editable so a document can always be deleted.
  const documentState = match(document.status)
    .with('processing', () => 'processing' as const)
    .with(P.union(...CODING_GUIDELINES_DOCUMENT_FAILED_PHASES), () => 'failed' as const)
    .otherwise(() => 'editable' as const)
  const originalName = document.display_name ?? ''
  const isDirty = displayName.trim() !== originalName
  const isNameEmpty = !displayName.trim()

  const showError = useShowMutationError({ prefix: 'Failed to save document', overrides: RENAME_ERROR_OVERRIDES })
  const patchMutation = usePatchCodingGuideline({ organizationId, onError: showError })

  const handleSave = () => {
    const id = document.id
    if (!id) {
      return
    }
    patchMutation.mutate(
      { id, body: { display_name: displayName.trim() } },
      {
        onSuccess: () => {
          addToast({ message: <>&ldquo;{displayName.trim()}&rdquo; was updated</>, variant: 'success' })
          onSuccess()
        },
      }
    )
  }

  // The floating footer must stay the immediate sibling of Dialog.Content (its slide-in
  // animation is driven by the `div[data-has-floating-footer] + &` selector), so the form
  // renders both via a fragment rather than letting the host place the footer separately.
  return (
    <>
      <Dialog.Content
        ref={drawerContentRef}
        aria-label="Edit coding guidelines"
        className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
        data-has-floating-footer={true}
        onInteractOutside={event => event.preventDefault()}
      >
        <Dialog.Title className={styles.title}>Edit document</Dialog.Title>
        <form className={styles.form} onSubmit={e => e.preventDefault()}>
          {match(documentState)
            .with('processing', () => (
              <div className={styles.processingBanner}>
                <WarningIcon size={18} className={styles.processingBannerIcon} />
                <p className={styles.processingBannerText}>
                  This document is still processing. Pixee is currently extracting and indexing this document. Once
                  complete, you&apos;ll be able to upload a replacement here.
                </p>
              </div>
            ))
            .with('failed', () => (
              <div className={styles.processingBanner}>
                <WarningCircleIcon size={18} weight="fill" className={styles.failedBannerIcon} />
                <p className={styles.failedBannerText}>
                  Pixee couldn&apos;t process this document. Delete it and upload a replacement.
                </p>
              </div>
            ))
            .with('editable', () => null)
            .exhaustive()}
          <DisplayNameField isDisabled={documentState !== 'editable'} />
          <InputContainer label="Document">
            <CurrentDocCard document={document} />
          </InputContainer>
        </form>
      </Dialog.Content>
      <FloatingFooter drawerContentRef={drawerContentRef}>
        {match(documentState)
          .with('processing', () => (
            <>
              <span />
              <GhostButton size="small" onClick={onCancel} className={styles.closeBtn}>
                Close
              </GhostButton>
            </>
          ))
          .with('failed', () => (
            <>
              <GhostButton type="destructive" size="small" onClick={onDelete} icon={<TrashSimpleIcon size={14} />}>
                Delete document
              </GhostButton>
              <GhostButton size="small" onClick={onCancel} className={styles.closeBtn}>
                Close
              </GhostButton>
            </>
          ))
          .with('editable', () => (
            <>
              <GhostButton
                type="destructive"
                size="small"
                state={patchMutation.isPending ? 'disabled' : 'default'}
                onClick={onDelete}
                icon={<TrashSimpleIcon size={14} />}
              >
                Delete document
              </GhostButton>
              <div className={styles.footerRight}>
                <GhostButton
                  size="small"
                  state={patchMutation.isPending ? 'disabled' : 'default'}
                  onClick={onCancel}
                  className={styles.closeBtn}
                >
                  Close
                </GhostButton>
                <DefaultButton
                  size="small"
                  state={patchMutation.isPending ? 'loading' : !isDirty || isNameEmpty ? 'disabled' : 'default'}
                  onClick={handleSave}
                  loadingLabel="Saving…"
                >
                  Save changes
                </DefaultButton>
              </div>
            </>
          ))
          .exhaustive()}
      </FloatingFooter>
    </>
  )
}

const CurrentDocCard: React.FC<{ document: CodingGuidelinesResponse }> = ({ document }) => {
  const docType = document.filename ? getDocType(document.filename) : undefined
  const uploadedAt = document.uploaded_at
    ? formatDate({ date: document.uploaded_at, type: 'mm/dd/yyyy-hh:mm' })
    : undefined

  const iconBadgeStyle = getDocTypeBadgeClass({
    docType,
    classes: {
      base: styles.currentDocIconBadge,
      pdf: styles.currentDocIconBadgePdf,
      docx: styles.currentDocIconBadgeDocx,
      md: styles.currentDocIconBadgeMd,
      txt: styles.currentDocIconBadgeTxt,
      html: styles.currentDocIconBadgeHtml,
    },
  })

  const subParts: string[] = []
  if (document.size) {
    subParts.push(formatFileSize(document.size))
  }
  if (uploadedAt) {
    subParts.push(`Uploaded ${uploadedAt}`)
  }

  return (
    <div className={styles.currentDocCard}>
      <span className={iconBadgeStyle} aria-hidden>
        <DocTypeIcon docType={docType} size={18} />
      </span>
      <div className={styles.currentDocMeta}>
        <p className={styles.currentDocLabel}>{document.filename ?? 'Unknown file'}</p>
        {subParts.length > 0 ? <p className={styles.currentDocSub}>{subParts.join(' · ')}</p> : null}
      </div>
    </div>
  )
}

const DisplayNameField: React.FC<{ isDisabled: boolean }> = ({ isDisabled }) => {
  const form = useFormContext()
  const displayName = useSelector(form.store, state => state.values.displayName)
  const [isTouched, setIsTouched] = useState(false)
  const hasError = isTouched && !displayName.trim()
  return (
    <TextInput
      id="edit-display-name"
      name="displayName"
      label="Name"
      isRequired
      value={displayName}
      disabled={isDisabled}
      isError={hasError}
      errorText={hasError ? 'A document name is required' : undefined}
      onChange={value => form.setFieldValue('displayName', value)}
      onBlur={() => setIsTouched(true)}
    />
  )
}
